import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  CREATE_MANUAL_VERIFICATION_BINDING,
  GET_MANUAL_VERIFICATION_BINDINGS,
  REMOVE_MANUAL_VERIFICATION_BINDING,
  get_request_config,
} from '@/utils/system_constants/constant';

export default {
  name: 'ManualVerificationBinding',
  mixins: [alert_mixin],
  props: {
    entityTypeId: {
      type: Number,
      required: false,
      default: null,
    },
    entityTypeName: {
      type: String,
      required: false,
      default: '',
    },
    entityConfigId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data: () => ({
    bindings: [],
    creating_binding: false,
    removing_binding_id: null,
    show_bind_modal: false,
    binding_form: {
      verification_type: 'kyc',
      verification_type_custom: '',
      cadence_kind: 'periodic',
      recurrence_interval_seconds: 31536000,
      grace_period_seconds: 1209600,
    },
  }),
  computed: {
    bindings_count() {
      return Array.isArray(this.bindings) ? this.bindings.length : 0;
    },
    verification_type_options() {
      return [
        { label: 'KYC', value: 'kyc' },
        { label: 'AML', value: 'aml' },
        { label: 'ID Verification', value: 'idv' },
        { label: 'Bank Account Proof', value: 'bank_account' },
        { label: 'Site Visit', value: 'site_visit' },
        { label: 'Custom...', value: '__custom__' },
      ];
    },
    cadence_kind_options() {
      return [
        { label: 'Periodic (repeats)', value: 'periodic' },
        { label: 'One-off (on demand)', value: 'one_off' },
      ];
    },
    recurrence_interval_options() {
      return [
        { label: 'Daily (1 day)', seconds: 86400 },
        { label: 'Weekly (7 days)', seconds: 604800 },
        { label: 'Bi-weekly (14 days)', seconds: 1209600 },
        { label: 'Monthly (30 days)', seconds: 2592000 },
        { label: 'Quarterly (90 days)', seconds: 7776000 },
        { label: 'Semi-annually (182 days)', seconds: 15724800 },
        { label: 'Annually (365 days)', seconds: 31536000 },
      ];
    },
    grace_period_options() {
      return [
        { label: '1 day (1 day)', seconds: 86400 },
        { label: '3 days (3 days)', seconds: 259200 },
        { label: '1 week (7 days)', seconds: 604800 },
        { label: '2 weeks (14 days)', seconds: 1209600 },
        { label: '1 month (30 days)', seconds: 2592000 },
      ];
    },
    is_periodic() {
      return String(this.binding_form?.cadence_kind || '') === 'periodic';
    },
    is_custom_type() {
      return String(this.binding_form?.verification_type || '') === '__custom__';
    },
  },
  mounted() {
    this.fetch_bindings();
  },

  methods: {
    format_seconds(value) {
      const seconds = Number(value);
      if (!Number.isFinite(seconds) || seconds < 0) return '0';
      return seconds.toLocaleString();
    },
    format_days_from_seconds(value) {
      const seconds = Number(value);
      if (!Number.isFinite(seconds) || seconds <= 0) return '—';
      const days = Math.round(seconds / 86400);
      const label = days === 1 ? 'day' : 'days';
      return `${days.toLocaleString()} ${label}`;
    },
    format_cadence_kind(value) {
      const cadence = String(value || '').trim().toLowerCase();
      if (cadence === 'one_off' || cadence === 'one-off') return 'one-off';
      if (cadence === 'periodic') return 'periodic';
      return cadence || '—';
    },
    fetch_bindings() {
      const entity_config_id = Number(this.entityConfigId);
      if (!Number.isFinite(entity_config_id) || entity_config_id <= 0) {
        this.bindings = [];
        return;
      }

      axios.get(
        GET_MANUAL_VERIFICATION_BINDINGS,
        get_request_config({ params: { entity_config_id } })
      )
        .then((response) => {
          if (response.data?.status !== 'success') {
            this.bindings = [];
            return;
          }

          this.bindings = (Array.isArray(response.data.data) ? response.data.data : [])
            .map((binding) => {
              return ({
                id: binding.id,
                verification_type: binding.verification_type,
                cadence_kind: binding.cadence_kind,
                recurrence_interval_seconds: binding.recurrence_interval_seconds,
                grace_period_seconds: binding.grace_period_seconds,
              });
            })
            .filter((binding) => binding.id);
        })
        .catch(() => {
          this.bindings = [];
        });
    },

    open_bind_modal() {
      this.binding_form = {
        verification_type: 'kyc',
        verification_type_custom: '',
        cadence_kind: 'periodic',
        recurrence_interval_seconds: 31536000,
        grace_period_seconds: 1209600,
      };
      this.show_bind_modal = true;
    },

    close_bind_modal() {
      this.show_bind_modal = false;
    },

    submit_binding() {
      if (this.creating_binding) return;

      const verification_type = this.is_custom_type
        ? String(this.binding_form?.verification_type_custom || '').trim()
        : String(this.binding_form?.verification_type || '').trim();
      if (!verification_type) {
        this.show_error('Please enter a verification type (e.g. kyc).');
        return;
      }

      const entity_config_id = Number(this.entityConfigId);
      if (!Number.isFinite(entity_config_id) || entity_config_id <= 0) {
        this.show_error('Missing entity configuration context.');
        return;
      }

      const payload = {
        entity_config_id,
        verification_type,
        cadence_kind: String(this.binding_form?.cadence_kind || 'periodic'),
        ...(this.is_periodic ? {
          recurrence_interval_seconds: Number(this.binding_form?.recurrence_interval_seconds) || 0,
          grace_period_seconds: Number(this.binding_form?.grace_period_seconds) || 0,
        } : {}),
      };

      this.creating_binding = true;

      axios.post(CREATE_MANUAL_VERIFICATION_BINDING, payload, get_request_config())
        .then((response) => {
          if (response.data?.status !== 'success') {
            this.show_error(response.data.message);
            return;
          }

          this.show_bind_modal = false;
          this.fetch_bindings();
          this.show_success(response.data.message);
        })
        .catch(() => {
          this.show_error('Failed to create manual verification binding.');
        })
        .finally(() => {
          this.creating_binding = false;
        });
    },

    remove_binding(binding) {
      const binding_id = Number(binding?.id);

      if (this.removing_binding_id === binding_id) return;

      this.show_confirm(
        'Remove manual verification binding?',
        'This will remove the manual verification rule from this entity configuration.',
        () => {
          this.removing_binding_id = binding_id;

          const payload = { binding_id };

          axios.post(REMOVE_MANUAL_VERIFICATION_BINDING, payload, get_request_config())
            .then((response) => {
              if (response.data?.status !== 'success') {
                this.show_error(response.data.message);
                return;
              }

              this.bindings = this.bindings.filter(
                (existing) => Number(existing?.id) !== binding_id
              );
              this.show_success(response.data.message);
            })
            .catch(() => {
              this.show_error('Failed to remove manual verification binding.');
            })
            .finally(() => {
              this.removing_binding_id = null;
            });
        }
      );
    },

    go_to_entity_overview() {
      this.$router.push({
        name: 'EntityConfigDetail',
        params: { entityTypeId: this.entityTypeId },
      });
    },
  },
};


